import { ReactNode } from 'react';

import { CSSObject, Theme, ThemeProvider } from '@mui/material/styles';
import '@mui/x-date-pickers/themeAugmentation';

type PickersThemeProviderProps = Readonly<{
  children: ReactNode;
}>;

// ==============================|| OUTLINED INPUT LOOK ||============================== //

// Mirrors the UI kit's MuiOutlinedInput overrides: pickers render their own PickersOutlinedInput,
// which the kit theme doesn't style.
function outlineStateStyles(theme: Theme, color: 'primary' | 'error'): CSSObject {
  return {
    '&:hover .MuiPickersOutlinedInput-notchedOutline': { borderColor: theme.palette[color].light },
    '&.Mui-focused': {
      boxShadow: theme.customShadows[color],
      '& .MuiPickersOutlinedInput-notchedOutline': { border: '1px solid', borderColor: theme.palette[color].light }
    }
  };
}

function withPickersOverrides(theme: Theme): Theme {
  return {
    ...theme,
    components: {
      ...theme.components,
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            paddingLeft: 12,
            ...outlineStateStyles(theme, 'primary'),
            '&.Mui-error': outlineStateStyles(theme, 'error'),
            '&.MuiPickersInputBase-inputSizeSmall': { fontSize: '0.75rem' }
          },
          notchedOutline: {
            borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[300]
          },
          // Doubled class so it outranks the base small-size `paddingTop: 1` variant, which emits after theme overrides.
          sectionsContainer: ({ ownerState }) => ({ '&&': { padding: ownerState.inputSize === 'small' ? '7.5px 0' : '10.5px 0' } })
        }
      }
    }
  };
}

// ==============================|| PICKERS THEME PROVIDER ||============================== //

export default function PickersThemeProvider({ children }: PickersThemeProviderProps) {
  return <ThemeProvider theme={withPickersOverrides}>{children}</ThemeProvider>;
}
